import React, { useMemo } from "react";
import "./ProfileCompletionHint.scss";

import avatarIcon from "./img/avatar.png";
import nameIcon from "./img/name.png";
import bioIcon from "./img/bio.png";
import listingIcon from "./img/listing.png";

const DEFAULT_AVATAR_URL =
  "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png";

const isDefaultUsername = (value) => {
  const v = String(value || "").trim();
  if (!v) return true;
  return /^user\d{5,}$/i.test(v);
};

const getProgressMessage = (completedCount, totalCount) => {
  const base = `${completedCount} з ${totalCount}`;

  if (completedCount === 0) {
    return `${base} · Почнемо - це займе кілька хвилин☺️`;
  }

  if (completedCount === 1) {
    return `${base} · Супер, ваш профіль оживає на очах🤗`;
  }

  if (completedCount === 2) {
    return `${base} · Гарно рухаєтесь, ще трішки😏`;
  }

  if (completedCount === 3) {
    return `${base} · Ще один крок - і готово✨`;
  }

  return `${base}`;
};

const ProfileCompletionHint = ({
  isOwner = false,
  username = "",
  avatar = "",
  bio = "",
  listingsCount = 0,
  listingsLoaded = false,
  onOpenEditProfile,
  onOpenCreateListing,
}) => {
  const steps = useMemo(() => {
    const hasCustomName = !isDefaultUsername(username);
    const hasCustomAvatar =
      !!avatar &&
      String(avatar).trim() !== "" &&
      String(avatar).trim() !== DEFAULT_AVATAR_URL;

    const hasBio = String(bio || "").trim().length > 0;
    const hasFirstListing = listingsLoaded ? Number(listingsCount || 0) > 0 : false;

    return [
      {
        key: "name",
        title: "Додайте ім’я",
        description: "Замість стандартного username поставте своє красиве ім’я✨",
        buttonText: "Змінити",
        done: hasCustomName,
        icon: nameIcon,
        action: onOpenEditProfile,
      },
      {
        key: "avatar",
        title: "Додайте фото",
        description: "Додайте своє фото, щоб профіль виглядав живим☺️",
        buttonText: "Обрати фото",
        done: hasCustomAvatar,
        icon: avatarIcon,
        action: onOpenEditProfile,
      },
      {
        key: "bio",
        title: "Додайте опис",
        description: "Розкажіть про себе, щоб люди краще вас зрозуміли👌🏻",
        buttonText: "Додати",
        done: hasBio,
        icon: bioIcon,
        action: onOpenEditProfile,
      },
      {
        key: "listing",
        title: "Перше оголошення",
        description: "Створіть перше оголошення — нехай люди це побачать🤗",
        buttonText: "Створити",
        done: hasFirstListing,
        icon: listingIcon,
        action: onOpenCreateListing,
        primary: true,
      },
    ];
  }, [
    username,
    avatar,
    bio,
    listingsCount,
    listingsLoaded,
    onOpenEditProfile,
    onOpenCreateListing,
  ]);

  const completedCount = steps.filter((step) => step.done).length;
  const progressPercent = Math.round((completedCount / steps.length) * 100);
  const visibleSteps = steps.filter((step) => !step.done);
  const progressMessage = getProgressMessage(completedCount, steps.length);

  if (!isOwner) return null;
  if (completedCount === steps.length) return null;

  return (
    <section className="profile-completion-hint" aria-label="Заповнення профілю">
      <div className="profile-completion-hint__top">
        <div className="profile-completion-hint__head">
          <h3>Заповніть профіль</h3>
          <p>{progressMessage}</p>
        </div>

        <div className="profile-completion-hint__progress">
          <div
            className="profile-completion-hint__progress-bar"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      <div className="profile-completion-hint__cards">
        {visibleSteps.map((step) => (
          <div className="profile-completion-card" key={step.key}>
            <div className="profile-completion-card__icon-wrap">
              <img
                src={step.icon}
                alt=""
                aria-hidden="true"
                className="profile-completion-card__icon"
              />
            </div>

            <div className="profile-completion-card__content">
              <h4>{step.title}</h4>
              <p>{step.description}</p>
            </div>

            <button
              type="button"
              className={`profile-completion-card__button ${
                step.primary ? "profile-completion-card__button--primary" : ""
              }`}
              onClick={step.action}
            >
              {step.buttonText}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ProfileCompletionHint;